/**
 * Homeowner billing card — shown on property dashboard for reseller-billed properties.
 * Shows monthly plan, provider brand, and "Manage Billing" link to Stripe Customer Portal.
 */
import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, ExternalLink } from 'lucide-react';
import { invokeAuthedFunction } from '@/lib/invokeAuthedFunction';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

const STATUS_BADGE: Record<string, { label: string; className: string }> = {
  active: { label: 'Active', className: 'bg-wt-green-primary/10 text-wt-green-primary border-wt-green-primary/20' },
  trialing: { label: 'Trial', className: 'bg-blue-500/10 text-blue-400 border-blue-500/20' },
  past_due: { label: 'Past Due', className: 'bg-wt-amber-primary/10 text-wt-amber-primary border-wt-amber-primary/20' },
  pending: { label: 'Pending', className: 'bg-blue-500/10 text-blue-400 border-blue-500/20' },
  canceled: { label: 'Canceled', className: 'bg-destructive/10 text-destructive border-destructive/20' },
};

interface HomeownerBillingCardProps {
  propertyId: string;
  priceCents: number | null;
  status: string;
  companyName?: string;
}

export function HomeownerBillingCard({
  propertyId,
  priceCents,
  status,
  companyName,
}: HomeownerBillingCardProps) {
  const [loading, setLoading] = useState(false);

  const handleManageBilling = async () => {
    setLoading(true);
    try {
      const data = await invokeAuthedFunction<{ portal_url?: string; error?: string }>(
        supabase,
        'manage-customer-billing',
        { property_id: propertyId }
      );
      if (data?.portal_url) {
        window.location.href = data.portal_url;
      } else {
        toast.error(data?.error || 'Could not open billing portal');
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to open billing portal');
    } finally {
      setLoading(false);
    }
  };

  const badge = STATUS_BADGE[status] || STATUS_BADGE.pending;

  return (
    <Card className="bg-wt-bg-secondary border-border">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium">Your Monitoring Plan</CardTitle>
          <Badge variant="outline" className={badge.className}>
            {badge.label}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {priceCents != null && (
          <p className="text-lg font-mono text-foreground">
            ${(priceCents / 100).toFixed(2)}
            <span className="text-sm text-muted-foreground font-sans">/month</span>
          </p>
        )}
        {companyName && (
          <p className="text-sm text-muted-foreground">
            Provided by {companyName}
          </p>
        )}
        {(status === 'active' || status === 'trialing' || status === 'past_due') && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleManageBilling}
            disabled={loading}
            className="gap-2"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <ExternalLink className="h-4 w-4" />
            )}
            Manage Billing
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
